import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "react-error-boundary";
import { LuEllipsis as LuMoreHorizontal } from "react-icons/lu";

import type { FocusNotification } from "@/backend/focus";
import {
  markNotificationAsRead,
  NotificationStatus,
  NotificationSubCategory,
  NotificationSubCategoryLabels,
} from "@/backend/focus";
import { DelegatedLink } from "@/components/shared/delegated-link";
import DesoAvatar from "@/components/shared/deso-avatar";
import { TimestampTooltip } from "@/components/shared/timestamp-tooltip";
import { UserDisplayNameWithStatus } from "@/components/shared/user-display-name-with-status";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import {
  useUnreadNotificationsSignalState,
  useUserAccountSignalState,
  useUserAuthSignalState,
} from "@/contexts/user-context";
import { FocusDateParser } from "@/utils/date";
import { formatDateTime } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import { getUserName } from "@/utils/usernames";

import {
  buildNotificationData,
  getNotificationSubCategoryResults,
  NotificationSubCategoryIcons,
  resolveNotificationHref,
} from "../notification-utils";
import { useNotificationsContext } from "../notifications-context";
import { NotificationPost } from "./notification-post";

type NotificationItemProps = {
  notification: FocusNotification;
  showUnreadIndicator?: boolean;
  markUnreadNotificationsAsRead?: boolean;
  relativeUsername?: string | null;
};

const DELAY_MARK_AS_READ = 250;

function NotificationItemFallback({ resetErrorBoundary }: FallbackProps) {
  return (
    <div
      className="
        p-4
        md:p-4
      "
    >
      <div className="text-center text-red-500">
        An error occurred while loading this notification.
        <button onClick={resetErrorBoundary} className="ml-2 underline">
          Retry
        </button>
      </div>
    </div>
  );
}

export const NotificationItemErrorBoundary = ({
  children,
}: {
  children: ReactNode;
}) => (
  <ErrorBoundary FallbackComponent={NotificationItemFallback}>
    {children}
  </ErrorBoundary>
);

export const NotificationItem = ({
  notification,
  showUnreadIndicator,
  markUnreadNotificationsAsRead,
  relativeUsername = null,
}: NotificationItemProps) => {
  const { currentUserPublicKey } = useUserAuthSignalState();
  const { setUnreadNotifications } = useUnreadNotificationsSignalState();
  const { decrementNotificationViewUnreadCounts } = useNotificationsContext();
  const notificationData = buildNotificationData(notification);
  const {
    ActorUser,
    Notification,
    Notification: { Subcategory, CreatedAt, Id, PostHashHex, Status },
    ActorUser: { Username, PublicKey, DisplayName },
  } = notificationData;
  const isNotificationUnread = Status !== NotificationStatus.READ;
  const [marked, setMarked] = useState(!isNotificationUnread);
  const notificationRef = useRef<HTMLDivElement>(null);
  const notificationResults = getNotificationSubCategoryResults(
    notificationData,
    {
      relativeUsername,
    },
  );
  const notificationHref = resolveNotificationHref(notificationData);

  useEffect(() => {
    if (!markUnreadNotificationsAsRead) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !marked) {
            const timer = setTimeout(async () => {
              await markNotificationAsRead(currentUserPublicKey, Id);
              setMarked(true);
              setUnreadNotifications((x) => Math.max(0, x - 1));
              decrementNotificationViewUnreadCounts(notification);
            }, DELAY_MARK_AS_READ);

            return () => clearTimeout(timer);
          }
        });
      },
      { threshold: 0.7 }, // 70% of the item is visible
    );

    if (notificationRef.current) {
      observer.observe(notificationRef.current);
    }

    return () => {
      if (notificationRef.current) {
        observer.unobserve(notificationRef.current);
      }
    };
  }, [
    Id,
    currentUserPublicKey,
    decrementNotificationViewUnreadCounts,
    marked,
    markUnreadNotificationsAsRead,
    notification,
    setUnreadNotifications,
  ]);

  const showPostContent = !!(
    [
      NotificationSubCategory.MENTION,
      NotificationSubCategory.POST_REPLY,
      NotificationSubCategory.POST_QUOTE_REPOST,
      NotificationSubCategory.UNLOCKABLE_CONTENT,
      NotificationSubCategory.POST_TIP,
      NotificationSubCategory.WHALE_POST_VIEW,
      NotificationSubCategory.POST_VIEW_THRESHOLD,
      NotificationSubCategory.POST_REPOST,
      NotificationSubCategory.REACTION_ANGRY,
      NotificationSubCategory.REACTION_ASTONISHED,
      NotificationSubCategory.REACTION_DISLIKE,
      NotificationSubCategory.REACTION_LAUGH,
      NotificationSubCategory.REACTION_LIKE,
      NotificationSubCategory.REACTION_LOVE,
      NotificationSubCategory.FOLLOWEE_NEW_POST,
      NotificationSubCategory.SUBSCRIBEE_NEW_POST,
    ].includes(Subcategory) && PostHashHex
  );

  const Icon = NotificationSubCategoryIcons[Subcategory] || (() => null);

  const formattedTimestamp = useMemo(() => {
    if (!CreatedAt) {
      return (
        <TimestampTooltip timestamp={FocusDateParser.now().toISOString()} />
      );
    }

    const diff = dayjs().diff(CreatedAt, "M");

    if (diff < 1) {
      return <TimestampTooltip timestamp={CreatedAt} />;
    }

    return formatDateTime(CreatedAt);
  }, [CreatedAt]);

  return (
    <DelegatedLink
      enabled={!!notificationHref}
      href={notificationHref ?? "/"}
      ref={notificationRef}
      className={cn("relative transition-colors duration-300", {
        "z-0 border-b border-border-light before:absolute before:inset-y-0 before:left-0 before:h-auto before:w-[2px] before:bg-primary before:content-[''] hover:before:bg-primary":
          showUnreadIndicator && !marked,
        "cursor-pointer hover:bg-accent": !!notificationHref,
      })}
    >
      <div
        className={cn(
          `
            flex items-start gap-4 p-4
            md:p-4
          `,
          {
            "z-0 border-b border-border-light": !showUnreadIndicator || marked,
          },
        )}
      >
        <div className="w-full">
          <div
            className="
              flex w-full items-start justify-between
              md:flex-row
            "
          >
            <div
              className="
                flex items-start gap-4 pr-4
                md:items-start
              "
            >
              <div
                className={cn(
                  `
                    relative top-1 hidden size-10 min-w-10 items-center
                    justify-center rounded-full border border-border-light
                    bg-background text-center transition-colors duration-300
                    sm:flex
                    md:top-auto
                  `,
                  !showUnreadIndicator || marked
                    ? "text-muted"
                    : "text-primary",
                )}
              >
                <Icon />
              </div>

              <div
                className="
                  flex flex-row gap-3
                  md:ml-0 md:flex-row md:items-start
                "
              >
                <DesoAvatar
                  account={{
                    publicKey: ActorUser?.PublicKey ?? "",
                    username: Username,
                    extraData: ActorUser?.ExtraData ?? {},
                  }}
                  size={40}
                  className="
                    size-[36px]
                    md:size-[38px]
                  "
                />
                <div className="flex flex-col items-start">
                  <div
                    className="
                      flex flex-col gap-2 text-xs font-semibold
                      text-muted-foreground
                    "
                  >
                    <div className="text-sm font-normal text-foreground">
                      <div
                        className={cn("w-full", {
                          "items-center gap-1 sm:flex": [
                            NotificationSubCategory.REACTION_LIKE,
                            NotificationSubCategory.REACTION_LAUGH,
                            NotificationSubCategory.REACTION_LOVE,
                            NotificationSubCategory.REACTION_DISLIKE,
                            NotificationSubCategory.REACTION_ANGRY,
                            NotificationSubCategory.REACTION_ASTONISHED,
                            NotificationSubCategory.REACTION_SAD,
                          ].includes(Subcategory),
                        })}
                      >
                        {!!PublicKey && (
                          <UserDisplayNameWithStatus
                            {...{
                              publicKey: PublicKey,
                              extraData: { DisplayName },
                              className: "text-sm font-sans inline-flex",
                              username: Username,
                              whaleSize: 16,
                            }}
                          />
                        )}{" "}
                        <span
                          className="
                            whitespace-pre-wrap
                            [&>p]:inline
                          "
                        >
                          {notificationResults.body}
                        </span>{" "}
                      </div>
                      <span className="text-xs text-muted">
                        {getUserName({
                          account: {
                            username: Username,
                          },
                        })}{" "}
                        • {formattedTimestamp}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {markUnreadNotificationsAsRead && (
              <NotificationActionsDropdown
                id={Id}
                {...{
                  Subcategory,
                  marked,
                  setMarked,
                }}
              />
            )}
          </div>
          {showPostContent &&
            (NotificationSubCategory.POST_REPLY === Subcategory ? (
              <NotificationPost
                {...{
                  postHash: Notification.ExtraData.ParentPostHashHex,
                  replyPostHash: PostHashHex,
                  notificationId: Id,
                }}
              />
            ) : (
              <NotificationPost
                {...{
                  postHash: PostHashHex,
                  notificationId: Id,
                  hideNotificationHeader: true,
                }}
              />
            ))}
        </div>
      </div>
    </DelegatedLink>
  );
};

export const NotificationActionsDropdown = ({
  id,
  Subcategory,
  marked,
  setMarked,
}: {
  id: string;
  Subcategory: NotificationSubCategory;
  marked: boolean;
  setMarked: (value: boolean) => void;
}) => {
  const { confirm } = useConfirm();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useUserAccountSignalState();
  const { currentUserPublicKey } = useUserAuthSignalState();
  const { setUnreadNotifications } = useUnreadNotificationsSignalState();
  const {
    decrementNotificationViewUnreadCounts,
    hideNotificationType,
    setNotifications,
  } = useNotificationsContext();

  const onMarkAsReadClick = async () => {
    if (marked || loading || !user?.publicKey) return;
    try {
      setLoading(true);
      await markNotificationAsRead(currentUserPublicKey, id);
      setUnreadNotifications((prev: number) => (prev === 0 ? 0 : prev - 1));
      setMarked(true);
      setNotifications((currentNotifications) => {
        const markedNotification = currentNotifications.find(
          (entry) => entry.Notification.Id === id,
        );

        if (markedNotification) {
          decrementNotificationViewUnreadCounts(markedNotification);
        }

        return currentNotifications;
      });
      toast({
        title: "Success",
        description: "Notification marked as read",
        variant: "success",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `An error happened when changing notification status`,
        details: createToastErrorDetails(e),
      });
    } finally {
      setLoading(false);
    }
  };

  const onHideNotificationClick = async () => {
    const confirmed = await confirm({
      description: `Are you sure you want to hide "${NotificationSubCategoryLabels[Subcategory]}" notifications?`,
    });

    if (!confirmed) return;

    try {
      setLoading(true);
      await hideNotificationType(Subcategory);
      toast({
        title: "Hidden",
        description: `"${NotificationSubCategoryLabels[Subcategory]}" notifications are hidden.`,
        variant: "success",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Notification type could not be hidden.",
        details: createToastErrorDetails(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <DropdownDrawer
        historyName="notification-actions"
        open={open}
        onOpenChange={setOpen}
        title="Notification actions"
      >
        <DropdownDrawerTrigger
          disabled={loading}
          render={
            <Button variant="outline" size="icon">
              <LuMoreHorizontal />
            </Button>
          }
        />
        <DropdownDrawerContent align="end">
          {!marked && (
            <DropdownDrawerItem onClick={onMarkAsReadClick}>
              <span>Mark as Read</span>
            </DropdownDrawerItem>
          )}
          <DropdownDrawerItem onClick={onHideNotificationClick}>
            <span>Hide Notification Type</span>
          </DropdownDrawerItem>
        </DropdownDrawerContent>
      </DropdownDrawer>
    </div>
  );
};
